import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Drawer,
  Descriptions,
  Alert,
  Tooltip,
  Empty,
  Input,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SettingOutlined,
  StopOutlined,
  LogoutOutlined,
  InboxOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '@/app/hooks';
import { selectHasPermission } from '@/features/auth/authSlice';
import {
  getEmployeeLifecycles,
  type EmployeeLifecycle,
} from '@/services/dataGovernanceService';
import {
  DryRunBanner,
  ComplianceStatusBadge,
  LegalHoldBadge,
  ConfirmDangerDialog,
} from '@/components/compliance';

// A single lifecycle transition the operator can request. `run` is a NO-OP today
// because the backend transition endpoints are stubbed — we only record intent.
type TransitionKey = 'deactivate' | 'terminate' | 'archive' | 'erasure';

interface TransitionDef {
  key: TransitionKey;
  label: string;
  icon: React.ReactNode;
  danger?: boolean;
  title: string;
  description: string;
  warning?: string;
  confirmLabel: string;
  // True when this transition must be blocked while a legal hold is active.
  legalHoldBlocks?: boolean;
}

const TRANSITIONS: TransitionDef[] = [
  {
    key: 'deactivate',
    label: 'Deactivate login',
    icon: <StopOutlined />,
    title: 'Deactivate login',
    description:
      'Disables the employee sign-in. Their data is retained; this does not delete anything.',
    confirmLabel: 'Deactivate login',
  },
  {
    key: 'terminate',
    label: 'Terminate employment',
    icon: <LogoutOutlined />,
    title: 'Terminate employment',
    description:
      'Marks employment as terminated. Payroll and tax records are retained for the statutory period.',
    confirmLabel: 'Terminate employment',
  },
  {
    key: 'archive',
    label: 'Archive employee',
    icon: <InboxOutlined />,
    title: 'Archive employee',
    description:
      'Moves the employee into an archived state. Records are preserved (read-only), never hard-deleted.',
    confirmLabel: 'Archive employee',
  },
  {
    key: 'erasure',
    label: 'Start erasure request',
    icon: <DeleteOutlined />,
    danger: true,
    title: 'Start erasure request',
    description:
      'Opens an erasure request. Retained payroll/tax records are anonymized rather than hard-deleted.',
    warning:
      'Erasure anonymizes or archives records that must be retained — it does not hard-delete a real employee.',
    confirmLabel: 'Start erasure request',
    legalHoldBlocks: true,
  },
];

const retentionEligibility = (row: EmployeeLifecycle): string =>
  row.has_payroll_records
    ? 'Retained: payroll/tax records must be kept'
    : 'Eligible for archive/anonymization';

const retainedList = (row: EmployeeLifecycle): string[] => {
  const items: string[] = [];
  if (row.has_payroll_records) items.push('Payroll / tax records');
  if (row.has_leave_records) items.push('Leave records');
  if (row.has_documents) items.push('Documents');
  if (row.legal_hold_active) items.push('Legal hold in effect');
  return items;
};

const EmployeeLifecyclePage: React.FC = () => {
  const canView = useAppSelector(selectHasPermission('employee_lifecycle:view'));
  const canUpdate = useAppSelector(selectHasPermission('employee_lifecycle:update'));

  const [rows, setRows] = useState<EmployeeLifecycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [selected, setSelected] = useState<EmployeeLifecycle | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pending, setPending] = useState<TransitionDef | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await getEmployeeLifecycles();
        if (alive) setRows(data);
      } catch (e: any) {
        message.error(
          e?.response?.data?.message || 'Could not load employee lifecycle.',
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.full_name.toLowerCase().includes(q) ||
        r.employee_id.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const openDrawer = (row: EmployeeLifecycle) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
    setPending(null);
  };

  // NO-OP: the backend lifecycle transition endpoints are stubbed. We record the
  // operator's intent only and never claim the transition actually happened.
  const runTransition = (_reason: string) => {
    setPending(null);
    message.info('Lifecycle transition endpoint pending — recorded intent only');
  };

  const columns: ColumnsType<EmployeeLifecycle> = [
    {
      title: 'Employee',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (name: string, row) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{name}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{row.employee_id}</span>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <ComplianceStatusBadge kind="employee_lifecycle" value={status} />
      ),
    },
    {
      title: 'Employment',
      dataIndex: 'employment_status',
      key: 'employment_status',
    },
    {
      title: 'Login',
      dataIndex: 'login_active',
      key: 'login_active',
      render: (active: boolean) =>
        active ? <Tag color="green">Active</Tag> : <Tag>Disabled</Tag>,
    },
    {
      title: 'Legal hold',
      dataIndex: 'legal_hold_active',
      key: 'legal_hold_active',
      render: (active: boolean) => <LegalHoldBadge active={active} />,
    },
    {
      title: 'Payroll',
      dataIndex: 'has_payroll_records',
      key: 'has_payroll_records',
      render: (has: boolean) =>
        has ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (v: string) => (v ? fmtDate(v) : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Button
          size="small"
          icon={<SettingOutlined />}
          onClick={() => openDrawer(row)}
        >
          Manage
        </Button>
      ),
    },
  ];

  if (!canView) {
    return (
      <Card>
        <Empty description="You do not have permission to view employee lifecycle." />
      </Card>
    );
  }

  const retained = selected ? retainedList(selected) : [];
  const hasRetained = retained.length > 0;
  const held = !!selected?.legal_hold_active;

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space direction="vertical" size={0}>
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              Employee Data Lifecycle
            </span>
            <span style={{ fontSize: 13, color: '#8c8c8c', fontWeight: 400 }}>
              Deactivation, termination, archive, and erasure — anonymize/archive,
              never hard-delete real employees.
            </span>
          </Space>
        }
      >
        <DryRunBanner />

        <div style={{ margin: '16px 0' }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search by name or employee ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 360 }}
          />
        </div>

        <Table<EmployeeLifecycle>
          rowKey="employee_id"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{
            emptyText: (
              <Empty description="No employees in a managed lifecycle state. Backend endpoint pending." />
            ),
          }}
        />
      </Card>

      <Drawer
        title={selected ? `Manage — ${selected.full_name}` : 'Manage'}
        width={520}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
      >
        {selected && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Current status">
                <ComplianceStatusBadge
                  kind="employee_lifecycle"
                  value={selected.status}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Employment status">
                {selected.employment_status}
              </Descriptions.Item>
              <Descriptions.Item label="Login">
                {selected.login_active ? (
                  <Tag color="green">Active</Tag>
                ) : (
                  <Tag>Disabled</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Payroll records">
                {selected.has_payroll_records ? 'Present' : 'None'}
              </Descriptions.Item>
              <Descriptions.Item label="Leave records">
                {selected.has_leave_records ? 'Present' : 'None'}
              </Descriptions.Item>
              <Descriptions.Item label="Documents">
                {selected.has_documents ? 'Present' : 'None'}
              </Descriptions.Item>
              <Descriptions.Item label="Legal hold">
                <LegalHoldBadge active={selected.legal_hold_active} />
              </Descriptions.Item>
              <Descriptions.Item label="Erasure status">
                {selected.erasure_status || 'None'}
              </Descriptions.Item>
              <Descriptions.Item label="Retention eligibility">
                {retentionEligibility(selected)}
              </Descriptions.Item>
            </Descriptions>

            {hasRetained && (
              <Alert
                type="warning"
                showIcon
                message="Retained records exist"
                description={
                  <>
                    <div>The following records are retained for this employee:</div>
                    <ul style={{ margin: '8px 0 0 16px' }}>
                      {retained.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                    <div style={{ marginTop: 8 }}>
                      Hard delete is avoided — these records are anonymized or
                      archived to satisfy statutory retention.
                    </div>
                  </>
                }
              />
            )}

            {held && (
              <Alert
                type="error"
                showIcon
                message="Blocked by Legal Hold — erasure and anonymization are disabled."
              />
            )}

            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {TRANSITIONS.map((t) => {
                const blockedByHold = !!t.legalHoldBlocks && held;
                const btn = (
                  <Button
                    block
                    danger={t.danger}
                    icon={t.icon}
                    disabled={!canUpdate || blockedByHold}
                    onClick={() => setPending(t)}
                  >
                    {t.label}
                  </Button>
                );
                return (
                  <React.Fragment key={t.key}>
                    {blockedByHold ? (
                      <Tooltip title="Blocked by Legal Hold">{btn}</Tooltip>
                    ) : (
                      btn
                    )}
                  </React.Fragment>
                );
              })}

              <Button
                block
                icon={<EyeOutlined />}
                onClick={() =>
                  message.info(
                    'Preview available from the Erasure Requests page once a request exists',
                  )
                }
              >
                Run erasure preview
              </Button>

              <Button
                block
                icon={<EyeOutlined />}
                onClick={() =>
                  message.info(
                    hasRetained
                      ? 'Retained records are listed above in this panel.'
                      : 'No retained records for this employee.',
                  )
                }
              >
                View retained records
              </Button>

              <Tooltip title="Not available in this release">
                <Button block icon={<DownloadOutlined />} disabled>
                  Download audit evidence
                </Button>
              </Tooltip>

              <Tooltip title="Hard delete is disabled for real employees. Use anonymization/archiving.">
                <Button block danger icon={<DeleteOutlined />} disabled>
                  Hard delete
                </Button>
              </Tooltip>
            </Space>
          </Space>
        )}
      </Drawer>

      <ConfirmDangerDialog
        open={!!pending}
        title={pending?.title || ''}
        description={pending?.description}
        warning={pending?.warning}
        confirmLabel={pending?.confirmLabel || 'Confirm'}
        requireReason
        onConfirm={runTransition}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

export default EmployeeLifecyclePage;
